import { useMutation } from '@apollo/client'
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react'

import { BulkCreatePostsImpressionDocument } from 'generated/resources/graphql'

const BATCH_INTERVAL_MS = 5_000
const MAX_BATCH_SIZE = 20

type PostImpressionTrackerContextType = {
  trackImpression: (postId: string) => void
  enabled: boolean
}

const PostImpressionTrackerContext = createContext<PostImpressionTrackerContextType | null>(null)

type PostImpressionTrackerProviderProps = {
  children: React.ReactNode
  enabled?: boolean
}

export const PostImpressionTrackerProvider = ({
  children,
  enabled = true,
}: PostImpressionTrackerProviderProps) => {
  const seenRef = useRef<Set<string>>(new Set())
  const batchRef = useRef<string[]>([])

  const [executeMutation] = useMutation(BulkCreatePostsImpressionDocument)

  const executeMutationRef = useRef(executeMutation)
  executeMutationRef.current = executeMutation

  const flushingRef = useRef(false)

  const flush = useCallback(() => {
    if (batchRef.current.length === 0 || flushingRef.current) return

    const postIds = [...batchRef.current]
    flushingRef.current = true

    executeMutationRef
      .current({ variables: { postIds } })
      .then(() => {
        batchRef.current = batchRef.current.filter((id) => !postIds.includes(id))
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console -- intentional error logging for failed analytics
        console.error('[PostImpressionTracker] Failed to send impressions:', error)
      })
      .finally(() => {
        flushingRef.current = false
      })
  }, [])

  useEffect(() => {
    if (!enabled) return

    const timer = setInterval(flush, BATCH_INTERVAL_MS)

    const handleBeforeUnload = () => flush()
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      clearInterval(timer)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      flush()
    }
  }, [flush, enabled])

  const trackImpression = useCallback(
    (postId: string) => {
      if (!enabled) return
      if (seenRef.current.has(postId)) return

      seenRef.current.add(postId)
      batchRef.current.push(postId)

      if (batchRef.current.length >= MAX_BATCH_SIZE) {
        flush()
      }
    },
    [flush, enabled]
  )

  return (
    <PostImpressionTrackerContext.Provider value={{ trackImpression, enabled }}>
      {children}
    </PostImpressionTrackerContext.Provider>
  )
}

const NOOP_CONTEXT: PostImpressionTrackerContextType = { trackImpression: () => {}, enabled: false }

export const usePostImpressionTracker = () => {
  return useContext(PostImpressionTrackerContext) ?? NOOP_CONTEXT
}
